import { ItemTypeEnum } from 'pages/project-dashboard/sections/schedule/sections/calendar/utils/types';
import { StatusValue } from 'react-dropzone-uploader';

export enum ClientPlatformEnum {
  WEB = 'WEB',
  MOBILE = 'MOBILE',
}

export interface IStyles {
  [key: string]: string | IStyles;
}
export interface IPaginationMeta {
  currentPage?: number;
  itemCount?: number;
  itemsPerPage?: number;
  totalItems?: number;
  totalPages?: number;
  nextCursor?: number | null;
}

export interface IDataList<T = unknown> {
  canRead?: boolean;
  items: T[];
  meta: IPaginationMeta;
}

export interface IRole {
  companyId: number;
  roleId: number;
  deletedAt: string | null;
  name: string | null;
  isOwner: boolean;
  isSubscription: boolean;
  jobTitle: string;
}

export interface IUserMe {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  avatar: string | null;
  userId: number;
  createdAt: string;
  deletedAt: string | null;
  timezone: string | null;
  isSuperAdmin: boolean;
  hasAccessToBeta: boolean;
  roles: IRole[] | null;
  isActive?: boolean;
}

export interface IInvite {
  id: number;
  email: string;
  title: string;
  companyRole: {
    id: number;
    name: string;
  };
  inviteProjects: {
    projectId: number;
  }[];
}

export enum PhaseTypesEnum {
  START = 'START',
  DEFAULT = 'DEFAULT',
  COMPLETE = 'COMPLETE',
}

export enum PhaseStatusesEnum {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  OVERDUE = 'OVERDUE',
}

export enum PredecessorsTypesEnum {
  START_TO_START = 'START-TO-START',
  START_TO_FINISH = 'START-TO-FINISH',
  FINISH_TO_START = 'FINISH-TO-START',
  FINISH_TO_FINISH = 'FINISH-TO-FINISH',
}

export enum PredecessorsLagTypesEnum {
  MINIMUM = 'MINIMUM',
  MAINTAIN = 'MAINTAIN',
}

export const PredecessorsTypes = {
  0: 'FINISH-TO-START',
  1: 'START-TO-START',
  2: 'FINISH-TO-FINISH',
  3: 'START-TO-FINISH',
};

export type TScheduleItemOptions = {
  label: string;
  value: number;
  disabled: boolean;
  disabledItemMessage: string;
  endDate: string;
  startDate: string;
}[];

export interface IPhaseLinkedItem {
  itemId: number;
  lagDays: number;
  lagType?: PredecessorsLagTypesEnum;
  predecessorId: number;
  taskDependency: PredecessorsTypesEnum;
  workDaysDifference?: number;
}

export interface IPhaseItemChecklist {
  completedItemsCount: number;
  id: number;
  itemsCount: number;
  name: string;
  phaseItemId: number;
}

export interface IPhaseItem<T = IPhaseLinkedItem> {
  id: number;
  phaseId: number;
  prevPhaseItemId: number;
  title: string;
  startDate: string | null;
  endDate: string | null;
  startDayDelay?: number | null;
  workDays: number | null;
  passedDays: number | null;
  isViewed: boolean;
  status: PhaseStatusesEnum;
  predecessors: T[];
  linkedItems: IPhaseLinkedItem[];
  checklist?: IPhaseItemChecklist | null;
  color?: string;
}

export interface IPhase {
  id: number;
  projectId: number;
  prevPhaseId?: number | null;
  title: string;
  color: string | null;
  endDate?: string | null;
  startDate?: string | null;
  workDays?: number;
  type: PhaseTypesEnum;
  status: PhaseStatusesEnum;
  items: IPhaseItem[];
  isView?: boolean;
}

export interface ITag {
  id: number;
  title: string;
  color?: string;
}

export type TDispatchTime = 'DAILY' | 'INSTANTLY' | 'none' | null;

export interface IPushNotificationSetting {
  id: number;
  dispatchTime: TDispatchTime;
  hour: number | null;
  notificationEvent:
  | 'NEW_POSTS_OR_COMMENTS_MENTIONS_TO_ACTIVITY_FEED'
  | 'NEW_POSTS_OR_COMMENTS_TO_ACTIVITY_FEED'
  | 'NEW_SCHEDULE_ITEMS'
  | 'CHANGES_IN_SCHEDULE_ITEMS'
  | 'ITEMS_BECOME_OVERDUE'
  | 'PHASE_ITEM_COMPLETION'
  | 'PROJECT_SELECTION_ITEMS_STATUS_REMINDER'
  | 'ON_SITE_ALERTS';
  isHidden: string | boolean;
}

export interface IEmailNotificationSetting {
  id: number;
  type: 'PROGRESS_SUMMARY';
  recurring: 'WEEKLY' | 'DAILY' | null;
  dayOfWeek: number | null;
  hour: number | null;
}

export interface IGanttPhaseItem {
  id: number | string;
  text: string;
  start_date: Date | undefined;
  end_date: Date | undefined;
  parent: number | string;
  constraint_type: string;
  constraint_date: Date | undefined;
  item_status: PhaseStatusesEnum;
  ownStartDate: string;
  ownEndDate: string;
  ownPredecessors: IPhaseLinkedItem[];
  ownLinkedItems: IPhaseLinkedItem[];
}

export interface IGanttUpdatePhaseItem {
  id?: number | string;
  text?: string;
  start_date?: Date | undefined;
  end_date?: Date | undefined;
  parent?: number | string;
  constraint_type?: string;
  constraint_date?: Date | undefined;
  item_status?: PhaseStatusesEnum;
  ownStartDate?: string;
  ownEndDate?: string;
  ownPredecessors?: IPhaseLinkedItem[];
  ownLinkedItems?: IPhaseLinkedItem[];
}

export interface IGanttPhase {
  id: number | string;
  text: string;
  open: boolean;
  type: string;
  color: string;
  end_date: Date | undefined;
  start_date: Date | undefined;
  item_status: PhaseStatusesEnum;
  ownStartDate: string;
  ownEndDate: string;
}

export interface ILinkPopper {
  source?: string;
  target?: string;
  valid: boolean;
}

export interface IUploadedFilesData {
  id: string;
  url: string;
  name: string;
}

export interface IFileUploadingUtilityProps {
  tempId: string;
  status: StatusValue;
  percent?: number;
  size?: number;
  mimeType?: string;
  previewUrl?: string;
  duration?: string;
  location?: string | null;
  timestamp?: string | null;
  description?: string | null;
}

export type IPhaseModalSliceProps = {
  isEdit?: boolean;
  openPhaseModal: boolean;
  updatedPhase?: IPhase | null;
};

export type IPhaseItemModalSliceProps = {
  openPhaseItemModal: boolean;
  updatedPhaseItem?: IPhaseItem<IPhaseLinkedItem>;
};

export type ICreateChecklistModalProps = {
  openCreateChecklistModal: boolean;
  fromGantt: boolean;
};

export interface IMatchingPhase extends IPhase {
  item_type: ItemTypeEnum;
}

export interface IMatchingPhaseItem extends IPhaseItem {
  item_type: ItemTypeEnum;
  color: string;
}

export type TEventsListPoppper = { isOpen: boolean; x: number; y: number };
export type TEventsList = (IMatchingPhase | IMatchingPhaseItem)[];

export type ICreatePhaseItemModalProps = {
  openCreatePhaseItemModal: IPhase;
  ganttItemId?: string;
};

export type IEditChecklistModalProps = {
  openEditChecklistModal: IPhaseItem<IPhaseLinkedItem>;
  fromGantt: boolean;
};

export type TSaveTemplateProps = {
  openSaveTemplateModal: boolean;
};

export type TCreateTemplateProps = {
  openCreateTemplateModal: boolean;
};
